/** Route guards for the authenticated shell (D4).
 *
 *  ProtectedRoute (moved here from App.tsx): unauthenticated visitors go to
 *  /login carrying the location they were on (state.from); when the logout
 *  was involuntary (client.ts refresh exhaustion -> AuthProvider's
 *  sessionExpired), the redirect adds ?reason=expired so LoginPage explains
 *  the sign-out. LoginRoute: after a successful login, return to the
 *  preserved URL instead of the hard-coded "/". state.from is an app path
 *  (appConfig.ts), so both guards also work under a base path. */
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth.ts'
import { appPath, stripAppBase } from '../../appConfig.ts'
import { LoginPage } from './LoginPage.tsx'

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, sessionExpired } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={appPath(sessionExpired ? '/login?reason=expired' : '/login')}
        replace
        state={{ from: stripAppBase(location.pathname) + location.search }}
      />
    )
  }

  return <>{children}</>
}

export function LoginRoute() {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from

  if (isAuthenticated) {
    return <Navigate to={appPath(from ?? '/')} replace />
  }
  return <LoginPage />
}
